import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api, apiErrorMessage } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { cn, formatCurrency } from "@/lib/utils";
import { Link, createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * `/accounting/inbox` — documents nobody typed in.
 *
 * Two ways in: photographed on a phone, or forwarded to Daniela's
 * mailbox. Either way the file is on the left and on the right a form
 * pre-filled with what the model read from it. Until the accountant
 * presses *Create document*, nothing is in the books — which is why
 * the whole page is built as confirming a proposal, not as a list of
 * finished documents.
 */

type Extraction = {
  summary: string | null;
  unreadable: string[];
  paymentMethod?: string | null;
  /** Present when the document carried its own data (ISDOC). */
  lines?: Array<{
    description: string;
    quantity: string;
    unitName: string | null;
    unitPrice: string;
    vatRate: string;
  }>;
  supplierCountry?: string | null;
} | null;

/** Every field is optional and explicitly `| undefined`: the form is
 *  built by patching, and `exactOptionalPropertyTypes` treats "absent"
 *  and "set to undefined" as different things. */
type DraftLine = {
  description: string;
  quantity: string;
  unitName: string;
  unitPrice: string;
  vatRate: string;
};

type Draft = {
  supplierName?: string | undefined;
  contactId?: string | undefined;
  kind?: "invoice" | "receipt" | "other" | "credit_note" | undefined;
  /** On a credit note: the invoice it corrects. */
  correctsNumber?: string | null | undefined;
  supplyType?:
    | "service"
    | "goods"
    | "goods_with_installation"
    | "construction"
    | "import"
    | "other"
    | undefined;
  domesticReverseCharge?: boolean | undefined;
  vatDeductible?: boolean | undefined;
  supplierCountry?: string | undefined;
  /** `false` when the document says the supplier is not a VAT payer. */
  supplierIsVatPayer?: boolean | undefined;
  vatMode?: "standard" | "reverse_charge" | undefined;
  exchangeRate?: string | undefined;
  receivedOn?: string | undefined;
  lines?: DraftLine[] | undefined;
  supplierRegistrationNo?: string | undefined;
  supplierVatNo?: string | undefined;
  number?: string | undefined;
  variableSymbol?: string | undefined;
  issuedOn?: string | undefined;
  taxableOn?: string | undefined;
  dueOn?: string | undefined;
  currency?: string | undefined;
  subtotal?: string | undefined;
  vatTotal?: string | undefined;
  total?: string | undefined;
  alreadyPaid?: boolean | undefined;
};

type ContactOption = {
  id: string;
  name: string;
  role: "customer" | "supplier" | "both";
  registrationNo: string | null;
  vatNo: string | null;
  country: string;
  defaultDueDays: number;
  currency: string;
};

type Match = {
  id: string;
  name: string;
  registrationNo: string | null;
  vatNo: string | null;
  country: string;
  defaultDueDays: number;
  currency: string;
};

type PaymentCandidate = {
  transactionId: string;
  bookedOn: string;
  amount: string;
  currency: string;
  card: { merchant: string; amount: string; currency: string } | null;
  payee: string | null;
  reasons: Array<
    "variable_symbol" | "amount" | "card_amount" | "supplier_name" | "date" | "together"
  >;
  parts: Array<{
    transactionId: string;
    bookedOn: string;
    amount: string;
    message: string | null;
  }> | null;
};

const PAYMENT_REASON: Record<PaymentCandidate["reasons"][number], string> = {
  variable_symbol: "variable symbol",
  amount: "amount",
  card_amount: "card amount",
  supplier_name: "supplier",
  date: "same day",
  together: "together make the amount",
};

/**
 * The payment to preselect, or none. A name-and-day match is only offered.
 * Of equally good ones the nearest to the invoice date wins, or either of two
 * that are the same charge twice.
 */
function preselectPayment(
  payments: PaymentCandidate[],
  issuedOn: string | null | undefined,
): string | null {
  const strong = payments.filter((p) => !p.reasons.includes("date"));
  const [first, second] = strong;
  if (!first) return null;
  if (!second || first.reasons.length > second.reasons.length) return first.transactionId;
  const issued = issuedOn ? new Date(issuedOn).getTime() : null;
  if (first.bookedOn === second.bookedOn && first.amount === second.amount)
    return first.transactionId;
  if (issued == null) return null;
  const gap = (p: PaymentCandidate) => Math.abs(new Date(p.bookedOn).getTime() - issued);
  return gap(second) - gap(first) >= 86_400_000 ? first.transactionId : null;
}

function paymentBody(payments: PaymentCandidate[], id: string | null) {
  const parts = payments.find((p) => p.transactionId === id)?.parts;
  return parts ? { transactionIds: parts.map((p) => p.transactionId) } : { transactionId: id };
}

type Duplicate = {
  kind: "expense" | "scan";
  id: string;
  label: string;
  reason: "same_number" | "same_amount_and_day";
};

type Scan = {
  id: string;
  status: "pending" | "extracted" | "failed" | "filed" | "discarded";
  source: "upload" | "email" | "fakturoid";
  senderEmail: string | null;
  subject: string | null;
  mimeType: string;
  note: string | null;
  capturedAt: string | null;
  createdAt: string;
  extractionError: string | null;
  extraction: Extraction;
  /** The model that read it, or `isdoc` when the document carried its
   *  own data and nothing was inferred. */
  extractionModel: string | null;
  draft: Draft;
  /** Who the address book thinks it is from. A proposal like the rest
   *  of the extraction — nothing is filled in from it silently. */
  match: Match | null;
  /** The archived file is no longer where the row says it is. */
  fileMissing: boolean;
  /** The same supplier document already in the books or in this queue. */
  duplicates: Duplicate[];
  /** The imported expense this reads again from its original; filing cancels it. */
  replaces?: {
    id: string;
    number: string | null;
    internalNumber: string | null;
    partnerName: string;
    total: string;
    currency: string;
  } | null;
  /** Bank movements that would pay it, best first. */
  payments: PaymentCandidate[];
  /** What doesn't add up in the reading (own VAT number, sums, customer, OSS tax). */
  warnings: string[];
  uploadedBy: { id: string; name: string } | null;
};

/**
 * The model answers in its tool's field names; a person reading the
 * queue should not have to. An unknown name is shown as it came rather
 * than dropped — a field we stopped translating is still information.
 */
const FIELD_LABEL: Record<string, string> = {
  supplier_name: "supplier",
  supplier_registration_no: "registration no.",
  supplier_vat_no: "VAT no.",
  document_number: "document number",
  variable_symbol: "variable symbol",
  issued_on: "issue date",
  taxable_on: "taxable supply date",
  due_on: "due date",
  currency: "currency",
  subtotal: "base",
  vat_total: "VAT",
  vat_rate: "VAT rate",
  total: "total",
  payment_method: "payment method",
  document_kind: "document type",
};

function fieldLabel(name: string): string {
  return FIELD_LABEL[name] ?? name.replace(/_/g, " ");
}

const INPUT =
  "h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type Mailbox = {
  configured: boolean;
  address: string;
  openToAnySender: boolean;
  lastRun: {
    status: "pending" | "running" | "completed" | "failed";
    finishedAt: string | null;
    error: string | null;
    result: {
      messages?: number;
      accepted?: number;
      skipped?: Array<{ from: string; subject: string; reason: string }>;
    } | null;
  } | null;
};

type FakturoidBox = {
  disconnected?: boolean;
  lastRun: {
    status: string;
    finishedAt: string | null;
    error: string | null;
    result: {
      files?: number;
      added?: number;
      skipped?: number;
      failed?: number;
      logs?: string[];
    } | null;
  } | null;
};

export const Route = createFileRoute("/_app/accounting/inbox")({
  loader: async () => {
    try {
      const [queue, mailbox, box, contacts, discarded] = await Promise.all([
        api.get<{ scans: Scan[]; total: number }>("/api/accounting/inbox?pageSize=50"),
        api.get<Mailbox>("/api/accounting/inbox/mailbox"),
        api.get<FakturoidBox>("/api/accounting/inbox/fakturoid"),
        api.get<{ contacts: ContactOption[] }>("/api/accounting/contacts"),
        api.get<{ scans: Scan[] }>("/api/accounting/inbox?status=discarded&pageSize=50"),
      ]);
      const duplicates = discarded.scans.filter((d) =>
        (d as Scan & { discardReason?: string | null }).discardReason?.startsWith("Duplicate"),
      );
      return { ...queue, mailbox, box, contacts: contacts.contacts, duplicates };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: InboxPage,
});

function InboxPage() {
  const { scans, total, mailbox, box, contacts, duplicates } = Route.useLoaderData();
  const router = useRouter();

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-semibold text-2xl">Incoming documents</h1>
        <p className="text-muted-foreground text-sm">
          What the model read is a proposal. The document only exists once you confirm it here.
        </p>
      </div>

      <DropBox onDone={() => router.invalidate()} />

      <RereadAll count={scans.length} onDone={() => router.invalidate()} />

      <SetAsideDuplicates scans={duplicates} onDone={() => router.invalidate()} />

      <MailboxPanel mailbox={mailbox} onDone={() => router.invalidate()} />

      {!box.disconnected && <FakturoidBoxPanel box={box} onDone={() => router.invalidate()} />}

      {scans.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground text-sm">
            The queue is empty. Documents arrive here from <code>/scan</code> on a phone, or by
            being forwarded to the mailbox above.
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-muted-foreground text-sm">{total} in the queue</p>
          {scans.map((scan) => (
            <ScanCard
              key={scan.id}
              scan={scan}
              contacts={contacts}
              onDone={() => router.invalidate()}
            />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * The third way in: a file from this computer.
 *
 * The phone has a camera and the mailbox has forwarded mail; a PDF
 * already sitting on a disk had no door until now, and printing it to
 * photograph it is not a workflow.
 */
function DropBox({ onDone }: { onDone: () => void }) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.set("file", file);
        const res = await fetch("/api/accounting/inbox", {
          method: "POST",
          body,
          credentials: "same-origin",
        });
        if (!res.ok) {
          const detail = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(detail?.error ?? `${file.name} was refused`);
        }
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The upload failed.");
    } finally {
      setBusy(false);
      setOver(false);
    }
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        void send(e.dataTransfer.files);
      }}
      className={cn(
        "rounded-lg border border-dashed p-6 text-center text-sm transition-colors",
        over ? "border-primary bg-primary/5" : "border-border",
      )}
    >
      <label className="cursor-pointer">
        <span className="font-medium underline">{busy ? "Uploading…" : "Upload a document"}</span>
        <input
          type="file"
          multiple
          accept="application/pdf,image/*"
          className="hidden"
          disabled={busy}
          onChange={(e) => void send(e.target.files)}
        />
      </label>
      <p className="mt-1 text-muted-foreground text-xs">
        Or drop a PDF or a photo anywhere on this box. It joins the same queue as the phone and the
        mailbox — read by the model, confirmed by you.
      </p>
      {error && <p className="mt-2 text-destructive text-xs">{error}</p>}
    </div>
  );
}

/**
 * The mailbox panel. It exists to answer the two questions people ask
 * about an address they forward to: is it working, and did my e-mail
 * arrive? The second one is why the refusals are listed — an invoice
 * that was turned away for an unknown sender otherwise just never
 * shows up.
 */
function MailboxPanel({ mailbox, onDone }: { mailbox: Mailbox; onDone: () => void }) {
  const check = useApiMutation(() => api.post("/api/accounting/inbox/mailbox/run", {}), {
    onSuccess: onDone,
    fallbackMessage: "Couldn't queue the read.",
  });
  const skipped = mailbox.lastRun?.result?.skipped ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">Forward an invoice by e-mail</CardTitle>
          <CardDescription>
            Send it to <span className="font-mono">{mailbox.address}</span> and Daniela reads the
            attachment into this queue. She files nothing — it is still you who confirms it.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={mailbox.configured ? "success" : "secondary"}>
            {mailbox.configured ? "watching" : "not set up"}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            disabled={!mailbox.configured || check.pending}
            onClick={() => check.mutate()}
          >
            {check.pending ? "Queueing…" : "Check now"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {!mailbox.configured && (
          <p className="text-muted-foreground">
            The mailbox has no password yet (<code>DOCUMENTS_IMAP_PASS</code>), so nothing is being
            read.
          </p>
        )}
        {mailbox.lastRun && (
          <p className="text-muted-foreground">
            Last read{" "}
            {mailbox.lastRun.finishedAt ? (
              <LocalDate value={mailbox.lastRun.finishedAt} withTime />
            ) : (
              "is running"
            )}
            : {mailbox.lastRun.result?.messages ?? 0} message(s),{" "}
            {mailbox.lastRun.result?.accepted ?? 0} document(s) taken.
          </p>
        )}
        {mailbox.lastRun?.error && <p className="text-destructive">{mailbox.lastRun.error}</p>}
        {skipped.length > 0 && (
          <div>
            <p className="text-amber-600">Not taken from the last read:</p>
            <ul className="mt-1 space-y-0.5 text-muted-foreground text-xs">
              {skipped.map((item) => (
                <li key={`${item.from}-${item.subject}`}>
                  {item.from || "(no sender)"} — {item.subject}: {item.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!mailbox.openToAnySender && (
          <p className="text-muted-foreground text-xs">
            Mail is taken from people with a Hub account and from addresses in the accounting
            contacts; anything else is turned away and listed here.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** Fakturoid's document box: files dropped there come into this queue. */
function FakturoidBoxPanel({ box, onDone }: { box: FakturoidBox; onDone: () => void }) {
  const pull = useApiMutation(() => api.post("/api/accounting/inbox/fakturoid/run", {}), {
    onSuccess: onDone,
    fallbackMessage: "Couldn't queue the pull.",
  });
  const run = box.lastRun;
  const problems = (run?.result?.logs ?? []).filter((line) => !line.includes('{"files"'));

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">Fakturoid's document box</CardTitle>
          <CardDescription>
            Files dropped into „Krabice na doklady“ come over every 30 minutes and stay in
            Fakturoid.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" disabled={pull.pending} onClick={() => pull.mutate()}>
          {pull.pending ? "Queueing…" : "Pull now"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {!run ? (
          <p className="text-muted-foreground">No pull has run yet.</p>
        ) : (
          <p className="text-muted-foreground">
            Last pull{" "}
            {run.finishedAt ? <LocalDate value={run.finishedAt} withTime /> : "is running"}:{" "}
            {run.result?.files ?? 0} file(s) in the box, {run.result?.added ?? 0} new here.
          </p>
        )}
        {run?.error && <p className="text-destructive">{run.error}</p>}
        {problems.length > 0 && (
          <ul className="space-y-0.5 text-amber-700 text-xs dark:text-amber-400">
            {problems.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {pull.error && <p className="text-destructive">{pull.error}</p>}
      </CardContent>
    </Card>
  );
}

/** The value the picker uses for "this supplier isn't in the book". */
const NOT_IN_BOOK = "__not_in_book__";

function ScanCard({
  scan,
  contacts,
  onDone,
}: {
  scan: Scan;
  contacts: ContactOption[];
  onDone: () => void;
}) {
  const [form, setForm] = useState<Draft>(() => ({
    ...(scan.draft ?? {}),
    // The draft's dates arrive as ISO instants (the server builds real
    // `Date`s from what the model read). A `<input type="date">` takes
    // `YYYY-MM-DD` and silently shows nothing for anything else — which
    // looked like the model had failed to read a date it had read fine.
    ...dateFields(scan.draft),
    // A document that came with its own data needs no guessing: its
    // lines and the supplier's country are facts, not readings.
    ...(scan.extraction?.lines?.length
      ? {
          lines: scan.extraction.lines.map((line) => ({
            description: line.description,
            quantity: line.quantity,
            unitName: line.unitName ?? "",
            unitPrice: line.unitPrice,
            vatRate: line.vatRate,
          })),
        }
      : {}),
    ...(scan.extraction?.supplierCountry
      ? { supplierCountry: scan.extraction.supplierCountry }
      : {}),
    // The address book's match is offered, not applied: the identity
    // below fills from it, and a glance at the picker says where the
    // registration number came from.
    ...(scan.match
      ? {
          contactId: scan.match.id,
          supplierName: scan.match.name,
          supplierRegistrationNo: scan.match.registrationNo ?? undefined,
          supplierVatNo: scan.match.vatNo ?? undefined,
          supplierCountry: scan.match.country,
        }
      : {}),
  }));
  const [reason, setReason] = useState("");
  const [fileAnyway, setFileAnyway] = useState(false);
  // One candidate, or a clear best one, is preselected; the person confirms or changes it.
  const [paymentId, setPaymentId] = useState<string | null>(() =>
    preselectPayment(scan.payments, scan.draft.issuedOn),
  );
  const [lateDuplicates, setLateDuplicates] = useState<Duplicate[] | null>(null);
  const duplicates = lateDuplicates ?? scan.duplicates;

  const file = useApiMutation(
    (body: Record<string, unknown>) =>
      api.post(`/api/accounting/inbox/${scan.id}/file`, {
        ...body,
        confirmDuplicate: fileAnyway,
        ...paymentBody(scan.payments, paymentId),
      }),
    {
      onSuccess: onDone,
      errorMessage: (err) => {
        // The corrected form can match something the model's reading didn't.
        if (err instanceof ApiError && err.status === 409) {
          const detail = err.detail as { duplicates?: Duplicate[] } | undefined;
          if (detail?.duplicates) setLateDuplicates(detail.duplicates);
          return "It looks like a document that is already here. Check it, or confirm below.";
        }
        return apiErrorMessage(err, "Couldn't create the document.");
      },
    },
  );
  const discard = useApiMutation(
    (why: string) => api.post(`/api/accounting/inbox/${scan.id}/discard`, { reason: why }),
    { onSuccess: onDone, errorMessage: () => "Couldn't discard it." },
  );
  const reread = useApiMutation(() => api.post(`/api/accounting/inbox/${scan.id}/reread`, {}), {
    onSuccess: onDone,
    errorMessage: () => "Couldn't start the read.",
  });

  const set = (key: keyof Draft) => (value: string) => setForm({ ...form, [key]: value });

  // Who accounts for the tax takes both halves: where the supplier is,
  // AND whether they charged Czech tax. A foreign supplier registered
  // here (or selling under the one-stop shop) charges it and accounts
  // for it themselves — self-assessing on top would put the same tax
  // on the return twice.
  const foreign = isForeign(form.supplierCountry, form.supplierVatNo);
  const chargedCzechVat = documentCarriesVat(form);
  const reverseCharge =
    form.vatMode === "reverse_charge" ||
    (form.vatMode === undefined && foreign && !chargedCzechVat);

  const lines = form.lines ?? [];
  const totals = totalsOf(lines, reverseCharge);
  // Lines win when there are any: they are what the document says, and
  // the totals below are then arithmetic, not a second opinion.
  const subtotal = lines.length > 0 ? totals.subtotal : Number(form.subtotal ?? 0);
  const vatTotal = lines.length > 0 ? totals.vat : Number(form.vatTotal ?? 0);
  const total = lines.length > 0 ? totals.total : Number(form.total ?? 0);

  const rate = useExchangeRate(form.currency, form.taxableOn ?? form.issuedOn);
  const { verdict, overridden } = useVatVerdict(form, form.vatMode);

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    file.mutate({
      contactId: form.contactId ?? null,
      supplierName: form.supplierName ?? "",
      supplierRegistrationNo: form.supplierRegistrationNo ?? null,
      supplierVatNo: form.supplierVatNo ?? null,
      supplierCountry: form.supplierCountry ?? null,
      supplierIsVatPayer: form.supplierIsVatPayer,
      kind: form.kind ?? "invoice",
      // The accountant's choice if they made one, the rules' otherwise.
      // A credit note's regime follows the invoice it corrects; the server decides.
      vatMode:
        form.kind === "credit_note"
          ? undefined
          : (form.vatMode ?? verdict?.mode ?? (reverseCharge ? "reverse_charge" : "standard")),
      correctsNumber: form.kind === "credit_note" ? (form.correctsNumber ?? null) : null,
      supplyType: form.supplyType ?? "service",
      domesticReverseCharge: form.domesticReverseCharge === true,
      vatDeductible: form.vatDeductible !== false,
      exchangeRate: Number(form.exchangeRate ?? rate.value ?? 1),
      number: form.number ?? null,
      variableSymbol: form.variableSymbol ?? null,
      issuedOn: form.issuedOn,
      taxableOn: form.taxableOn ?? null,
      dueOn: form.dueOn ?? form.issuedOn,
      receivedOn: form.receivedOn ?? null,
      currency: form.currency ?? "CZK",
      subtotal,
      vatTotal,
      total,
      lines: lines.map((line) => ({
        description: line.description,
        quantity: Number(line.quantity || 1),
        unitName: line.unitName || null,
        unitPrice: Number(line.unitPrice || 0),
        vatRate: Number(line.vatRate || 0),
      })),
      alreadyPaid: form.alreadyPaid === true,
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">
            {scan.extraction?.summary ?? "Scanned document"}
          </CardTitle>
          <CardDescription>
            {scan.source === "email" ? (
              <>
                by e-mail from {scan.senderEmail ?? "unknown"}
                {scan.subject && ` — „${scan.subject}"`}
              </>
            ) : scan.source === "fakturoid" ? (
              <>from Fakturoid's document box{scan.note && ` (${scan.note})`}</>
            ) : (
              (scan.uploadedBy?.name ?? "unknown")
            )}{" "}
            · <LocalDate value={scan.capturedAt ?? scan.createdAt} withTime />
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {scan.status === "pending" && <Badge variant="secondary">reading…</Badge>}
          {scan.status === "failed" && <Badge variant="destructive">unreadable</Badge>}
          <Button
            variant="outline"
            size="sm"
            disabled={reread.pending}
            onClick={() => reread.mutate()}
          >
            Read again
          </Button>
        </div>
      </CardHeader>

      <CardContent className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          {scan.fileMissing ? (
            <MissingFile scan={scan} onDone={onDone} />
          ) : scan.mimeType === "application/pdf" ? (
            // A PDF is not an image; an <img> would render a broken
            // icon and hide the one thing being confirmed against.
            <object
              data={`/api/accounting/inbox/${scan.id}/photo`}
              type="application/pdf"
              className="h-[32rem] w-full rounded-lg border"
              aria-label="The document"
            >
              <a
                className="underline"
                href={`/api/accounting/inbox/${scan.id}/photo`}
                target="_blank"
                rel="noreferrer"
              >
                Open the PDF
              </a>
            </object>
          ) : (
            <img
              src={`/api/accounting/inbox/${scan.id}/photo`}
              alt="The document"
              className="w-full rounded-lg border object-contain"
            />
          )}
          {scan.extractionError && (
            <p className="text-destructive text-xs">{scan.extractionError}</p>
          )}
          {scan.extraction?.unreadable && scan.extraction.unreadable.length > 0 && (
            // What the model couldn't read should be seen before the
            // accountant hunts for it in empty fields.
            <p className="text-amber-600 text-xs">
              On the document but unreadable:{" "}
              {scan.extraction.unreadable.map(fieldLabel).join(", ")}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap items-center gap-1">
            <span className="mr-2 text-muted-foreground text-xs">Document type</span>
            {(["invoice", "receipt", "credit_note", "other"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setForm((current) => ({ ...current, kind }))}
                className={cn(
                  "rounded-md border px-3 py-1 text-sm capitalize",
                  (form.kind ?? "invoice") === kind
                    ? "border-primary bg-primary/10 text-primary"
                    : "text-muted-foreground",
                )}
              >
                {kind === "credit_note" ? "credit note" : kind}
              </button>
            ))}
          </div>

          {form.kind === "credit_note" && (
            <div className="space-y-1">
              <Field
                label="Corrects invoice no."
                value={form.correctsNumber ?? undefined}
                onChange={set("correctsNumber")}
              />
              <p className="text-muted-foreground text-xs">
                Amounts are what comes back; Hub keeps them negative. A note for exactly the tax on
                its invoice is a refund of tax and stays off the VAT return; any other follows the
                invoice.
              </p>
            </div>
          )}

          <SupplierPicker
            contacts={contacts}
            form={form}
            read={scan.draft.supplierName ?? null}
            onPick={(patch) => setForm((current) => ({ ...current, ...patch }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Registration no."
              value={form.supplierRegistrationNo}
              onChange={set("supplierRegistrationNo")}
            />
            <Field label="VAT no." value={form.supplierVatNo} onChange={set("supplierVatNo")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Document number" value={form.number} onChange={set("number")} />
            <Field
              label="Variable symbol"
              value={form.variableSymbol}
              onChange={set("variableSymbol")}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Issued"
              type="date"
              required
              value={form.issuedOn}
              onChange={set("issuedOn")}
            />
            <Field
              label="Taxable supply"
              type="date"
              value={form.taxableOn}
              onChange={set("taxableOn")}
            />
            <Field label="Due" type="date" value={form.dueOn} onChange={set("dueOn")} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field
              label="Received"
              type="date"
              value={form.receivedOn}
              onChange={set("receivedOn")}
            />
            <Field label="Currency" value={form.currency ?? "CZK"} onChange={set("currency")} />
            <Field
              label="Rate to CZK"
              type="number"
              value={form.exchangeRate ?? (rate.value != null ? String(rate.value) : undefined)}
              onChange={set("exchangeRate")}
            />
          </div>
          {(form.currency ?? "CZK").toUpperCase() !== "CZK" && (
            <p className="text-muted-foreground text-xs">
              {rate.state === "loading"
                ? "Looking up the ČNB fixing…"
                : rate.state === "missing"
                  ? "The ČNB has no fixing for that day — fill the rate in from the bank statement."
                  : `ČNB fixing for the taxable date: ${rate.value ?? "—"} CZK.`}
            </p>
          )}

          {form.kind !== "credit_note" && (
            <VatPanel
              form={form}
              verdict={verdict}
              overridden={overridden}
              reverseCharge={reverseCharge}
              onPick={(patch) => setForm((current) => ({ ...current, ...patch }))}
            />
          )}

          <LineEditor
            lines={lines}
            reverseCharge={reverseCharge}
            currency={form.currency ?? "CZK"}
            onChange={(next) => setForm((current) => ({ ...current, lines: next }))}
            onSeed={() =>
              setForm((current) => ({
                ...current,
                lines: [
                  {
                    description: scan.extraction?.summary ?? "Supply per document",
                    quantity: "1",
                    unitName: "",
                    unitPrice: current.subtotal ?? current.total ?? "0",
                    vatRate: current.vatTotal && current.subtotal ? "21" : "0",
                  },
                ],
              }))
            }
          />

          {lines.length === 0 && (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Base" type="number" value={form.subtotal} onChange={set("subtotal")} />
              <Field label="VAT" type="number" value={form.vatTotal} onChange={set("vatTotal")} />
              <Field
                label="Total"
                type="number"
                required
                value={form.total}
                onChange={set("total")}
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm" htmlFor={`paid-${scan.id}`}>
            <input
              id={`paid-${scan.id}`}
              type="checkbox"
              checked={form.alreadyPaid === true}
              onChange={(e) => setForm({ ...form, alreadyPaid: e.target.checked })}
              className="h-4 w-4"
            />
            Paid in cash on the spot
          </label>

          {scan.warnings.length > 0 && (
            <ul className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm dark:bg-amber-950/30 dark:text-amber-200">
              {scan.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          <div className="space-y-1 rounded-md border p-3 text-sm">
            <p className="font-medium">Paid from the bank</p>
            {scan.extraction?.paymentMethod === "prepaid" && scan.payments.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                Paid from a prepaid credit: the card only topped the credit up, so no movement pays
                this document. Create it unpaid.
              </p>
            ) : scan.payments.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                No bank movement fits yet (amount, date around the invoice). Create it unpaid; it
                pairs once the payment shows up.
              </p>
            ) : (
              <ul className="space-y-1">
                {scan.payments.map((p) => (
                  <li key={p.transactionId}>
                    <label
                      className="flex flex-wrap items-center gap-2"
                      htmlFor={`pay-${scan.id}-${p.transactionId}`}
                    >
                      <input
                        id={`pay-${scan.id}-${p.transactionId}`}
                        type="radio"
                        name={`pay-${scan.id}`}
                        checked={paymentId === p.transactionId}
                        onChange={() => setPaymentId(p.transactionId)}
                      />
                      <LocalDate value={p.bookedOn} />
                      <span className="tabular-nums">
                        {p.amount} {p.currency}
                      </span>
                      {p.card ? (
                        <span className="text-muted-foreground text-xs">
                          card · {p.card.merchant} · {p.card.amount} {p.card.currency}
                        </span>
                      ) : (
                        p.payee && <span className="text-muted-foreground text-xs">{p.payee}</span>
                      )}
                      {p.reasons.map((r) => (
                        <Badge key={r} variant="secondary" className="text-[10px]">
                          {PAYMENT_REASON[r]}
                        </Badge>
                      ))}
                    </label>
                    {p.parts && (
                      <ul className="text-muted-foreground ml-6 text-xs">
                        {p.parts.map((part) => (
                          <li key={part.transactionId}>
                            <LocalDate value={part.bookedOn} /> · {part.amount} {p.currency}
                            {part.message && ` · ${part.message}`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
                <li>
                  <label
                    className="flex items-center gap-2 text-xs"
                    htmlFor={`pay-${scan.id}-none`}
                  >
                    <input
                      id={`pay-${scan.id}-none`}
                      type="radio"
                      name={`pay-${scan.id}`}
                      checked={paymentId === null}
                      onChange={() => setPaymentId(null)}
                    />
                    none of these, create it unpaid
                  </label>
                </li>
              </ul>
            )}
          </div>

          {scan.replaces && (
            <p className="rounded-md border border-sky-500/40 bg-sky-500/5 p-2 text-sm">
              Read again from the original of{" "}
              <Link
                to="/accounting/expenses/$id"
                params={{ id: scan.replaces.id }}
                className="underline"
              >
                {scan.replaces.number ?? scan.replaces.internalNumber}
              </Link>{" "}
              ({scan.replaces.partnerName}, {scan.replaces.total} {scan.replaces.currency} as
              Fakturoid had it). Creating this document cancels that one and takes over its
              payments.
            </p>
          )}
          {duplicates.length > 0 && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
              <p className="font-medium text-amber-800 dark:text-amber-300">
                This may already be here:
              </p>
              <ul className="space-y-1">
                {duplicates.map((d) => (
                  <li key={`${d.kind}-${d.id}`}>
                    {d.kind === "expense" ? (
                      <Link
                        className="underline"
                        to="/accounting/expenses/$id"
                        params={{ id: d.id }}
                      >
                        {d.label}
                      </Link>
                    ) : (
                      d.label
                    )}{" "}
                    <span className="text-muted-foreground">
                      ({d.reason === "same_number" ? "same number" : "same amount and day"})
                    </span>
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-2" htmlFor={`anyway-${scan.id}`}>
                <input
                  id={`anyway-${scan.id}`}
                  type="checkbox"
                  checked={fileAnyway}
                  onChange={(e) => setFileAnyway(e.target.checked)}
                  className="h-4 w-4"
                />
                It is a different document, create it anyway
              </label>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={file.pending || (duplicates.length > 0 && !fileAnyway)}>
              {file.pending ? "Creating…" : "Create document"}
            </Button>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for discarding"
              className="h-9 w-40 rounded-md border border-input bg-background px-2 text-sm"
            />
            <Button
              type="button"
              variant="outline"
              disabled={reason.trim().length < 2 || discard.pending}
              onClick={() => discard.mutate(reason.trim())}
            >
              Discard
            </Button>
          </div>
          {(file.error || discard.error) && (
            <p className="text-destructive text-sm">{file.error ?? discard.error}</p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Who the document is from.
 *
 * The address book first, free text second — because the registration
 * and VAT numbers are things the book knows and a receipt usually
 * doesn't print. Picking a contact fills the identity from it and ties
 * the filed document to that contact's page; "not in the address book"
 * leaves the name as the model read it and files the document without
 * a partner.
 */
/**
 * The file this row was made from is gone.
 *
 * For a document that arrived by e-mail that is recoverable: Hub marks
 * mail read, it never deletes it, so the original is still sitting in
 * the mailbox. For an upload it is not — the phone that sent it has
 * long since moved on.
 */
function MissingFile({ scan, onDone }: { scan: Scan; onDone: () => void }) {
  // `fallbackMessage`, not `errorMessage`: a constant message throws the
  // server's reason away, and the reason is the whole point here —
  // "that message is no longer in the mailbox" and "the mailbox has no
  // password" need different things done about them.
  const refetch = useApiMutation(() => api.post(`/api/accounting/inbox/${scan.id}/refetch`, {}), {
    onSuccess: onDone,
    fallbackMessage: "Couldn't fetch it again.",
  });

  return (
    <div className="space-y-2 rounded-lg border border-amber-300 border-dashed p-6 text-sm">
      <p className="font-medium text-amber-700">The archived file is missing.</p>
      {scan.source === "email" ? (
        <>
          <p className="text-muted-foreground">
            The e-mail is still in the mailbox, so it can be pulled back — the row keeps its place
            in the queue and whatever the model already read.
          </p>
          <Button size="sm" disabled={refetch.pending} onClick={() => refetch.mutate()}>
            {refetch.pending ? "Fetching…" : "Fetch again from the mailbox"}
          </Button>
        </>
      ) : (
        <p className="text-muted-foreground">
          {scan.source === "fakturoid"
            ? "It came from Fakturoid's document box. Download it there, discard this row and upload the file again."
            : "It came from an upload, so there is nothing to fetch it back from — discard the row and send the document again."}
        </p>
      )}
      {refetch.error && <p className="text-destructive">{refetch.error}</p>}
    </div>
  );
}

/** The date-only half of a draft, in the shape a date input accepts. */
function dateFields(draft: Draft | undefined): Partial<Draft> {
  const day = (value: string | undefined) => (value ? value.slice(0, 10) : undefined);
  return {
    ...(draft?.issuedOn ? { issuedOn: day(draft.issuedOn) } : {}),
    ...(draft?.taxableOn ? { taxableOn: day(draft.taxableOn) } : {}),
    ...(draft?.dueOn ? { dueOn: day(draft.dueOn) } : {}),
    ...(draft?.receivedOn ? { receivedOn: day(draft.receivedOn) } : {}),
  };
}

/** EU + the rest: anything that isn't Czech is ours to assess. Mirrors
 *  `services/accounting/vat/reverse-charge.ts`; the server decides, this
 *  only shows what it will decide. */
function isForeign(country: string | undefined, vatNo: string | undefined): boolean {
  const stated = (country ?? "").trim().toUpperCase();
  if (stated.length === 2) return stated !== "CZ";
  const prefix = (vatNo ?? "").trim().toUpperCase().slice(0, 2);
  return /^[A-Z]{2}$/.test(prefix) ? prefix !== "CZ" : false;
}

type Verdict = {
  mode: string;
  selfAssess: boolean;
  deductible: boolean;
  returnLines: { tax: string[]; deduction: string[] };
  controlSection: string | null;
  rule: string;
  law: string | null;
  reason: string;
  needsReview: boolean;
};

/**
 * What the rules make of the document as it stands. Asked again on
 * every change that can matter, because the answer is the thing being
 * confirmed — not a detail of it.
 */
function useVatVerdict(form: Draft, chosenMode: Draft["vatMode"]) {
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const facts = JSON.stringify({
    supplierCountry: form.supplierCountry ?? null,
    supplierIsVatPayer: form.supplierIsVatPayer,
    supplierVatNo: form.supplierVatNo ?? null,
    statedVat: Number(form.vatTotal ?? 0),
    total: Number(form.total ?? 0),
    supplyType: form.supplyType ?? "service",
    domesticReverseCharge: form.domesticReverseCharge === true,
  });

  useEffect(() => {
    let live = true;
    api
      .post<Verdict>("/api/accounting/vat/classify", JSON.parse(facts))
      .then((res) => live && setVerdict(res))
      .catch(() => live && setVerdict(null));
    return () => {
      live = false;
    };
  }, [facts]);

  // An explicit choice by the accountant wins over the rules, and the
  // panel says so rather than quietly showing a verdict that is no
  // longer what will be filed.
  return { verdict, overridden: chosenMode !== undefined };
}

/** Does the document itself show Czech tax? Lines win when there are
 *  any, the same way the totals do. */
function documentCarriesVat(form: Draft): boolean {
  const lines = form.lines ?? [];
  if (lines.length > 0) {
    return lines.some((line) => Number(line.vatRate || 0) > 0);
  }
  return Number(form.vatTotal ?? 0) > 0;
}

function totalsOf(
  lines: DraftLine[],
  reverseCharge: boolean,
): { subtotal: number; vat: number; total: number } {
  let subtotal = 0;
  let vat = 0;
  for (const line of lines) {
    const base = Number(line.quantity || 0) * Number(line.unitPrice || 0);
    subtotal += base;
    vat += (base * Number(line.vatRate || 0)) / 100;
  }
  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    subtotal: round(subtotal),
    vat: round(vat),
    // Under reverse charge the supplier billed the base only — the tax
    // goes to the tax office, not into what we owe them.
    total: round(reverseCharge ? subtotal : subtotal + vat),
  };
}

/**
 * The CNB fixing for the taxable date. Asked for only when it can
 * matter (a foreign currency and a date), and it never fills the field
 * behind the person's back — it offers what it found.
 */
function useExchangeRate(currency: string | undefined, on: string | undefined) {
  const [value, setValue] = useState<number | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "missing">("idle");

  useEffect(() => {
    const code = (currency ?? "CZK").toUpperCase();
    if (code === "CZK" || !on) {
      setValue(null);
      setState("idle");
      return;
    }
    let live = true;
    setState("loading");
    api
      .get<{ rate: number | null }>(
        `/api/accounting/exchange-rate?currency=${encodeURIComponent(code)}&on=${encodeURIComponent(on)}`,
      )
      .then((res) => {
        if (!live) return;
        setValue(res.rate);
        setState(res.rate === null ? "missing" : "idle");
      })
      .catch(() => live && setState("missing"));
    return () => {
      live = false;
    };
  }, [currency, on]);

  return { value, state };
}

/**
 * The document's own lines.
 *
 * Hidden until asked for: most receipts are a single amount and a table
 * of one row is noise. Once there are lines they are the truth and the
 * totals underneath follow from them — two places to type the same
 * number is how a document ends up disagreeing with itself.
 */
const SUPPLY_LABEL: Record<string, string> = {
  service: "a service",
  goods: "goods",
  goods_with_installation: "goods with installation",
  construction: "construction work",
  import: "imported goods",
  other: "something else",
};

/**
 * What the rules make of this document, and the two facts they need
 * that nothing can read off the paper: what kind of supply it is, and
 * whether a Czech supplier moved the tax to us.
 *
 * It shows the verdict with its section of the Act rather than a
 * yes/no, because the accountant is the one answering for it — and it
 * says plainly when it doesn't know instead of picking something.
 */
function VatPanel({
  form,
  verdict,
  overridden,
  reverseCharge,
  onPick,
}: {
  form: Draft;
  verdict: Verdict | null;
  overridden: boolean;
  reverseCharge: boolean;
  onPick: (patch: Partial<Draft>) => void;
}) {
  const domestic = !isForeign(form.supplierCountry, form.supplierVatNo);

  return (
    <div className="space-y-2 rounded-md border p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">This is</span>
        {(["service", "goods", "goods_with_installation", "construction", "import"] as const).map(
          (type) => (
            <button
              key={type}
              type="button"
              onClick={() => onPick({ supplyType: type })}
              className={cn(
                "rounded border px-2 py-0.5",
                (form.supplyType ?? "service") === type
                  ? "border-primary bg-primary/10 text-primary"
                  : "text-muted-foreground",
              )}
            >
              {SUPPLY_LABEL[type]}
            </button>
          ),
        )}
      </div>

      {domestic && (
        <label className="flex items-center gap-2 text-muted-foreground">
          <input
            type="checkbox"
            checked={form.domesticReverseCharge === true}
            onChange={(e) => onPick({ domesticReverseCharge: e.target.checked })}
            className="h-3.5 w-3.5"
          />
          The document says „daň odvede zákazník" (§92a)
        </label>
      )}

      <label className="flex items-center gap-2 text-muted-foreground">
        <input
          type="checkbox"
          checked={form.vatDeductible === false}
          onChange={(e) => onPick({ vatDeductible: !e.target.checked })}
          className="h-3.5 w-3.5"
        />
        Don't deduct the VAT (not made out to the company): it stays in the cost
      </label>

      {verdict && (
        <div
          className={cn(
            "rounded-md border-l-2 pl-2",
            verdict.needsReview ? "border-amber-500" : "border-emerald-500",
          )}
        >
          <p className="font-medium">
            {verdict.selfAssess ? "We assess the tax" : "The supplier accounts for the tax"}
            {verdict.law && <span className="text-muted-foreground"> · {verdict.law}</span>}
            {verdict.needsReview && <span className="text-amber-700"> · needs a look</span>}
          </p>
          <p className="mt-0.5 text-muted-foreground">{verdict.reason}</p>
          {(verdict.returnLines.tax.length > 0 || verdict.controlSection) && (
            <p className="mt-0.5 text-muted-foreground">
              Return{" "}
              {verdict.returnLines.tax.length > 0
                ? `lines ${verdict.returnLines.tax.join("/")}`
                : "— deduction only"}
              {verdict.returnLines.deduction.length > 0 &&
                `, deduction ${verdict.returnLines.deduction.join("/")}`}
              {verdict.controlSection && ` · control statement ${verdict.controlSection}`}
            </p>
          )}
        </div>
      )}

      <p className="text-muted-foreground">
        {overridden ? (
          <>
            You set this to {reverseCharge ? "self-assessed" : "the supplier's tax"} by hand.{" "}
            <button
              type="button"
              className="underline"
              onClick={() => onPick({ vatMode: undefined })}
            >
              Use the rules again
            </button>
          </>
        ) : (
          <>
            Disagree?{" "}
            <button
              type="button"
              className="underline"
              onClick={() => onPick({ vatMode: reverseCharge ? "standard" : "reverse_charge" })}
            >
              {reverseCharge ? "The supplier accounts for it" : "We assess it"}
            </button>
          </>
        )}
      </p>
    </div>
  );
}

function LineEditor({
  lines,
  reverseCharge,
  currency,
  onChange,
  onSeed,
}: {
  lines: DraftLine[];
  reverseCharge: boolean;
  currency: string;
  onChange: (lines: DraftLine[]) => void;
  onSeed: () => void;
}) {
  const totals = totalsOf(lines, reverseCharge);
  const money = (n: number) => formatCurrency(n, currency.toUpperCase());

  if (lines.length === 0) {
    return (
      <button type="button" className="text-sm underline" onClick={onSeed}>
        Break it into lines
      </button>
    );
  }

  const patch = (index: number, key: keyof DraftLine, value: string) =>
    onChange(lines.map((line, i) => (i === index ? { ...line, [key]: value } : line)));

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {lines.map((line, index) => (
          <div
            key={`line-${index}-${line.description}`}
            className="space-y-1 rounded-md border p-2"
          >
            <input
              value={line.description}
              onChange={(e) => patch(index, "description", e.target.value)}
              placeholder="Description"
              className={INPUT}
            />
            <div className="grid grid-cols-5 gap-2">
              <input
                value={line.quantity}
                onChange={(e) => patch(index, "quantity", e.target.value)}
                placeholder="Qty"
                className={INPUT}
              />
              <input
                value={line.unitName}
                onChange={(e) => patch(index, "unitName", e.target.value)}
                placeholder="Unit"
                className={INPUT}
              />
              <input
                value={line.unitPrice}
                onChange={(e) => patch(index, "unitPrice", e.target.value)}
                placeholder="Unit price"
                className={`${INPUT} col-span-2`}
              />
              <input
                value={line.vatRate}
                onChange={(e) => patch(index, "vatRate", e.target.value)}
                placeholder="VAT %"
                className={INPUT}
              />
            </div>
            <button
              type="button"
              className="text-muted-foreground text-xs underline"
              onClick={() => onChange(lines.filter((_, i) => i !== index))}
            >
              remove line
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="text-sm underline"
        onClick={() =>
          onChange([
            ...lines,
            { description: "", quantity: "1", unitName: "", unitPrice: "0", vatRate: "21" },
          ])
        }
      >
        Add a line
      </button>

      <table className="ml-auto text-sm">
        <tbody>
          <tr>
            <td className="pr-6 text-muted-foreground">Base</td>
            <td className="text-right tabular-nums">{money(totals.subtotal)}</td>
          </tr>
          <tr>
            <td className="pr-6 text-muted-foreground">
              {reverseCharge ? "VAT we assess" : "VAT"}
            </td>
            <td className="text-right tabular-nums">{money(totals.vat)}</td>
          </tr>
          <tr className="border-t">
            <td className="pr-6 font-medium">
              {reverseCharge ? "Payable to the supplier" : "Total"}
            </td>
            <td className="text-right font-semibold tabular-nums">{money(totals.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SupplierPicker({
  contacts,
  form,
  read,
  onPick,
}: {
  contacts: ContactOption[];
  form: Draft;
  /** The name the model read, kept so picking a contact is reversible. */
  read: string | null;
  onPick: (patch: Partial<Draft>) => void;
}) {
  const router = useRouter();
  const add = useApiMutation(
    () =>
      api.post<{ id: string; name: string }>("/api/accounting/contacts", {
        name: form.supplierName?.trim() ?? "",
        role: "supplier",
        registrationNo: form.supplierRegistrationNo ?? null,
        vatNo: form.supplierVatNo ?? null,
        ...(form.supplierCountry ? { country: form.supplierCountry } : {}),
        ...(form.currency ? { currency: form.currency } : {}),
      }),
    {
      onSuccess: (contact) => {
        onPick({ contactId: contact.id, supplierName: contact.name });
        router.invalidate();
      },
      fallbackMessage: "Couldn't add the supplier.",
    },
  );
  const options = [
    { value: NOT_IN_BOOK, label: "— not in the address book —" },
    ...contacts
      .filter((contact) => contact.role !== "customer")
      .map((contact) => ({
        value: contact.id,
        label: contact.name,
        ...(contact.registrationNo ? { hint: contact.registrationNo } : {}),
      })),
    // A partner we also sell to is still a possible supplier; it just
    // isn't the first thing to offer.
    ...contacts
      .filter((contact) => contact.role === "customer")
      .map((contact) => ({ value: contact.id, label: contact.name })),
  ];

  return (
    <div className="space-y-2 md:col-span-3">
      <span className="text-muted-foreground text-xs">Supplier</span>
      <Combobox
        options={options}
        value={form.contactId ?? NOT_IN_BOOK}
        aria-label="Supplier"
        placeholder="Pick from the address book…"
        onChange={(value) => {
          if (value === NOT_IN_BOOK) {
            onPick({
              contactId: undefined,
              supplierName: read ?? form.supplierName ?? "",
            });
            return;
          }
          const contact = contacts.find((c) => c.id === value);
          if (!contact) return;
          onPick({
            contactId: contact.id,
            supplierName: contact.name,
            supplierRegistrationNo: contact.registrationNo ?? undefined,
            supplierVatNo: contact.vatNo ?? undefined,
            currency: form.currency ?? contact.currency,
          });
        }}
      />
      {!form.contactId && (
        <>
          <label className="block text-sm">
            <span className="text-muted-foreground text-xs">Name on the document</span>
            <input
              value={form.supplierName ?? ""}
              onChange={(e) => onPick({ supplierName: e.target.value })}
              required
              className={INPUT}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-muted-foreground">
              Not in the address book: it is added when the document is created.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!form.supplierName?.trim() || add.pending}
              onClick={() => add.mutate()}
            >
              {add.pending ? "Adding…" : "Add to the address book now"}
            </Button>
            {add.error && <span className="text-destructive">{add.error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="text-muted-foreground text-xs">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </div>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT}
      />
    </div>
  );
}

/** Reads every waiting document again, after the reading itself improved. */
function RereadAll({ count, onDone }: { count: number; onDone: () => void }) {
  const [left, setLeft] = useState<number | null>(null);
  const reading = left !== null;
  const reread = useApiMutation(
    () => api.post<{ queued: number }>("/api/accounting/inbox/reread-all", {}),
    {
      onSuccess: (res) => setLeft(res.queued),
      fallbackMessage: "Couldn't queue the reading.",
    },
  );

  // Poll the queue while documents are still being read, then reload the list.
  useEffect(() => {
    if (!reading) return;
    const timer = setInterval(async () => {
      try {
        const { total } = await api.get<{ total: number }>(
          "/api/accounting/inbox?status=pending&pageSize=1",
        );
        setLeft(total);
        if (total === 0) {
          clearInterval(timer);
          setLeft(null);
          onDone();
        }
      } catch {
        // A missed poll is retried on the next tick.
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [reading, onDone]);

  if (count === 0 && left === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Button
        variant="outline"
        size="sm"
        disabled={reread.pending || left !== null}
        onClick={() => {
          if (
            window.confirm(
              `Read all ${count} waiting documents again? Edits in their forms are lost.`,
            )
          ) {
            reread.mutate();
          }
        }}
      >
        {reread.pending ? "Queueing…" : left !== null ? `Reading… ${left} left` : "Read all again"}
      </Button>
      <span className="text-muted-foreground text-xs">
        {left !== null
          ? "The list reloads when the last one is read."
          : "Reads the waiting documents again (supplier country, VAT payer, customer)."}
      </span>
      {reread.error && <span className="text-destructive text-xs">{reread.error}</span>}
    </div>
  );
}

/** Documents Hub put aside as the same one it already has; one click brings one back. */
function SetAsideDuplicates({ scans, onDone }: { scans: Scan[]; onDone: () => void }) {
  const restore = useApiMutation(
    (id: string) => api.post(`/api/accounting/inbox/${id}/restore`, {}),
    { onSuccess: onDone, fallbackMessage: "Couldn't bring it back." },
  );
  if (scans.length === 0) return null;
  return (
    <details className="rounded-md border p-3 text-sm">
      <summary className="cursor-pointer">Set aside as duplicates ({scans.length})</summary>
      <ul className="mt-2 space-y-1">
        {scans.map((scan) => (
          <li key={scan.id} className="flex flex-wrap items-center gap-2">
            <span>{scan.extraction?.summary ?? scan.note ?? "document"}</span>
            <span className="text-muted-foreground text-xs">
              {(scan as Scan & { discardReason?: string | null }).discardReason}
            </span>
            <button
              type="button"
              className="text-muted-foreground text-xs underline"
              disabled={restore.pending}
              onClick={() => restore.mutate(scan.id)}
            >
              bring back
            </button>
          </li>
        ))}
      </ul>
      {restore.error && <p className="text-destructive text-xs">{restore.error}</p>}
    </details>
  );
}
